import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import InviteHeader from './components/InviteHeader';
import InviteForm from './components/InviteForm';
import Icon from '../../components/AppIcon';

const AcceptInvite = () => {
  const navigate = useNavigate();

  const [pageState, setPageState] = useState('loading'); // loading | valid | invalid | success
  const [session, setSession] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [organizationName, setOrganizationName] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initInvite = async () => {
      try {
        // Parse the URL hash for the invite token
        const hash = window.location?.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const accessToken = params?.get('access_token');
        const refreshToken = params?.get('refresh_token') || '';
        const type = params?.get('type');

        let currentSession = null;

        if (accessToken && type === 'invite') {
          // Exchange the invite token for a real session
          const { data, error } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          if (error) throw error;
          currentSession = data?.session;
        } else {
          // Fallback: check if a session already exists
          const { data: { session: existingSession } } = await supabase?.auth?.getSession();
          currentSession = existingSession;
        }

        if (!currentSession?.user) {
          setGeneralError('This invitation link is invalid or has expired. Please ask your admin to resend the invitation.');
          setPageState('invalid');
          return;
        }

        setSession(currentSession);
        const user = currentSession?.user;

        // Check if user already has a profile (already accepted)
        const { data: existingProfile } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name')
          ?.eq('id', user?.id)
          ?.maybeSingle();

        if (existingProfile?.id) {
          // Already set up — redirect to dashboard
          navigate('/dashboard');
          return;
        }

        // Look up pending invite by email to get org + role details
        const { data: pendingInvite } = await supabase
          ?.from('pending_invites')
          ?.select('*')
          ?.eq('email', user?.email)
          ?.maybeSingle();

        // Pull metadata from auth user (set when invite was created)
        const userMeta = user?.user_metadata || {};

        const resolvedInviteData = {
          fullName: pendingInvite?.full_name || userMeta?.full_name || '',
          role: pendingInvite?.role || userMeta?.role || 'viewer',
          organizationId: pendingInvite?.organization_id || userMeta?.organization_id || null,
          pendingInviteId: pendingInvite?.id || null,
          phone: pendingInvite?.phone || null,
          department: pendingInvite?.department || null,
          jobTitle: pendingInvite?.job_title || null
        };

        setInviteData(resolvedInviteData);

        // Fetch organization name for welcome message
        if (resolvedInviteData?.organizationId) {
          const { data: org } = await supabase
            ?.from('organizations')
            ?.select('name')
            ?.eq('id', resolvedInviteData?.organizationId)
            ?.maybeSingle();
          if (org?.name) setOrganizationName(org?.name);
        }

        setPageState('valid');
      } catch (err) {
        console.error('AcceptInvite init error:', err);
        setGeneralError('Something went wrong while loading your invitation. Please try again.');
        setPageState('invalid');
      }
    };

    initInvite();
  }, [navigate]);

  const handleCompleteSetup = async (formData) => {
    setLoading(true);
    setGeneralError('');

    try {
      const user = session?.user;
      if (!user) throw new Error('Session expired. Please click the invitation link again.');

      // 1. Update the auth user's password and name
      const { error: updateAuthError } = await supabase?.auth?.updateUser({
        password: formData?.password,
        data: { full_name: formData?.fullName }
      });

      if (updateAuthError) {
        throw updateAuthError;
      }

      // 2. Create user_profiles record
      const profilePayload = {
        id: user?.id,
        email: user?.email,
        full_name: formData?.fullName,
        role: inviteData?.role || 'viewer',
        status: 'active',
        organization_id: inviteData?.organizationId || null,
        phone: inviteData?.phone || null,
        department: inviteData?.department || null,
        job_title: inviteData?.jobTitle || null,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.insert(profilePayload);

      if (profileError) {
        // If profile already exists (race condition), try upsert
        if (profileError?.code === '23505') {
          const { error: upsertError } = await supabase
            ?.from('user_profiles')
            ?.update({
              full_name: formData?.fullName,
              role: inviteData?.role || 'viewer',
              status: 'active',
              organization_id: inviteData?.organizationId || null,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', user?.id);
          if (upsertError) throw upsertError;
        } else {
          throw profileError;
        }
      }

      // 3. Remove the pending invite record (cleanup)
      if (inviteData?.pendingInviteId) {
        await supabase
          ?.from('pending_invites')
          ?.delete()
          ?.eq('id', inviteData?.pendingInviteId);
      } else {
        // Fallback: delete by email
        await supabase
          ?.from('pending_invites')
          ?.delete()
          ?.eq('email', user?.email);
      }

      // 4. Sign out so they log in fresh with their new password
      await supabase?.auth?.signOut();

      // 5. Redirect to login with success message
      navigate('/login', {
        state: {
          message: 'Account setup complete! Please sign in with your new password.',
          email: user?.email
        }
      });
    } catch (err) {
      console.error('Complete setup error:', err);
      if (err?.message?.includes('session')) {
        setGeneralError('Your session has expired. Please click the invitation link in your email again.');
      } else if (err?.message?.includes('weak') || err?.message?.includes('password')) {
        setGeneralError('Password is too weak. Please choose a stronger password with uppercase, lowercase, and numbers.');
      } else {
        setGeneralError(err?.message || 'Failed to complete account setup. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4 animate-pulse">
            <Icon name="Mail" size={32} color="#FFFFFF" />
          </div>
          <p className="text-muted-foreground">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-error rounded-2xl mb-4">
              <Icon name="AlertCircle" size={32} color="#FFFFFF" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Invalid Invitation</h1>
            <p className="text-muted-foreground mb-6">{generalError}</p>
            <button
              onClick={() => navigate('/login')}
              className="text-accent hover:text-accent/80 font-medium transition-colors text-sm"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <InviteHeader organizationName={organizationName} />

        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          {/* General Error */}
          {generalError && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{generalError}</p>
            </div>
          )}

          <InviteForm
            email={session?.user?.email}
            inviteData={inviteData}
            onSubmit={handleCompleteSetup}
            loading={loading}
          />
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-accent hover:text-accent/80 font-medium transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
